/*
================================================================================
File: src/pages/Settings/MyInvestor.js
Description: Form for updating the user's investor profile.
================================================================================
*/
import React, { useState } from "react";
import Select from "react-tailwindcss-select";
import instance from "../../api/axios";
import PageContent from "../../components/common/PageContent";
import {
  propOption,
  attributeOptions,
  countiesOptions,
  financeOptions,
  boolOptions,
} from "../../constants/options";
import moment from "moment";

// SVG Icons
const UserIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
  </svg>
);

const DollarIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12zm.01-6a1 1 0 10-2 0 1 1 0 012 0z" clipRule="evenodd" />
  </svg>
);

const ChecklistIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
  </svg>
);

const MyInvestor = ({ pulldata, user }) => {
  const [fullName, setFullName] = useState(user.app_name);
  const [phoneNumber, setPhoneNumber] = useState(user.phone);
  const [website, setWebsite] = useState(user.websites);
  const [emailAddress, setEmailAddress] = useState(user.inv_email);
  const [minInvestment, setMinInvestment] = useState(parseInt(user.money_from));
  const [maxInvestment, setMaxInvestment] = useState(parseInt(user.money_to));
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const [wantsToLendMoney, setWantsToLendMoney] = useState({
    value: user.lend_cash,
    label: user.lend_cash === "0" ? "No" : "Yes",
  });
  const [error, setError] = useState(null);

  const [financeTypes, setFinanceTypes] = useState(
    (user.fincance_type ? user.fincance_type.split(", ") : []).map((val) => ({
      value: val,
      label: val,
    }))
  );
  const [propertyTypes, setPropertyTypes] = useState(
    (user.prop_types ? user.prop_types.split(", ") : []).map((val) => ({
      value: val,
      label: val,
    }))
  );
  const [attributes, setAttributes] = useState(
    (user.investment_types ? user.investment_types.split(", ") : []).map(
      (val) => ({ value: val, label: val })
    )
  );
  const [investmentCounties, setInvestmentCounties] = useState(
    (user.counties ? user.counties.split(", ") : []).map((val) => ({
      value: val,
      label: val,
    }))
  );

  const handleSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData();

    formData.append("type", "investor");
    formData.append("owner", user.app_id);
    formData.append("pid", user.app_id);
    formData.append("timestamp", moment().unix());
    formData.append("device", "web");
    formData.append("name", fullName);
    formData.append("number", phoneNumber);
    formData.append("email", emailAddress);
    formData.append("website", website);
    formData.append("max_price", maxInvestment);
    formData.append("min_price", minInvestment);
    formData.append(
      "types",
      attributes?.map((item) => item.value).join(", ") || ""
    );
    formData.append(
      "counties",
      investmentCounties?.map((item) => item.value).join(", ") || ""
    );
    formData.append(
      "prop_fin_types",
      financeTypes?.map((item) => item.value).join(", ") || ""
    );
    formData.append("lend_cash", wantsToLendMoney.value);
    formData.append(
      "prop_types",
      propertyTypes?.map((item) => item.value).join(", ") || ""
    );
    if (logoFile) {
      formData.append("imageName", logoFileName);
      formData.append("imageData", logoFile);
    }

    try {
      await instance.post("/updateProfile.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating investor info: " + e.response.data.message);
        console.error(
          "Error updating investor info: ",
          e.response.data.message
        );
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsLoading(false);
            await pulldata(user?.app_id);
    }
  };

  return (
    <PageContent
      title="Profile Settings"
      subtitle="Investor Profile"
      error={error}
    >
      <div className="p-8 space-y-6">
        {/* Hero Banner */}
        <div className="bg-emerald-600 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-white/20 rounded-lg">
              <DollarIcon />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Investor Profile</h2>
              <p className="text-emerald-100">Define your investment preferences so leads know what you're looking for.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Section A: Identity */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
              <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                <UserIcon />
              </div>
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Identity</h3>
            </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Full Name:
                  </label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Phone #:
                  </label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    E-Mail Address:
                  </label>
                  <input
                    type="email"
                    value={emailAddress}
                    onChange={(e) => setEmailAddress(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Website:
                  </label>
                  <input
                    type="text"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Investor Image (Optional):
                  </label>
                  <input
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        setLogoFile(e.target.files[0]);
                        setLogoFileName(e.target.files[0].name);
                      }
                    }}
                    type="file"
                    name="imageData"
                    id="imageData"
                    accept="image/png, image/jpeg, image/gif"
                    className="dark:text-gray-200 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                  />
                </div>
              </div>
            </div>

            {/* Section B: Investment Range */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <DollarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Investment Range</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Min. Investment Range $:
                  </label>
                  <input
                    type="number"
                    value={minInvestment}
                    onChange={(e) => setMinInvestment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Max. Investment Range $:
                  </label>
                  <input
                    type="number"
                    value={maxInvestment}
                    onChange={(e) => setMaxInvestment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Lend Money
                  </label>
                  <Select
                    value={wantsToLendMoney}
                    onChange={setWantsToLendMoney}
                    options={boolOptions}
                    classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-emerald-500 focus:ring focus:ring-emerald-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-emerald-500"
          : "text-gray-500 hover:bg-emerald-100 hover:text-emerald-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                  />
                </div>
              </div>
            </div>

            {/* Section C: Investment Criteria */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                  <ChecklistIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Investment Criteria</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Property Types
                  </label>
                  <Select
                    isMultiple
                    isSearchable
                    value={propertyTypes}
                    onChange={setPropertyTypes}
                    options={propOption}
                    classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-teal-500"
          : "text-gray-500 hover:bg-teal-100 hover:text-teal-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Attributes</label>
                  <Select
                    isMultiple
                    isSearchable
                    value={attributes}
                    onChange={setAttributes}
                    options={attributeOptions}
                    classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-teal-500"
          : "text-gray-500 hover:bg-teal-100 hover:text-teal-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Investment Counties</label>
                  <Select
                    isMultiple
                    isSearchable
                    value={investmentCounties}
                    onChange={setInvestmentCounties}
                    options={countiesOptions}
                    classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-teal-500"
          : "text-gray-500 hover:bg-teal-100 hover:text-teal-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                    Finance Types</label>
                  <Select
                    isMultiple
                    isSearchable
                    value={financeTypes}
                    onChange={setFinanceTypes}
                    options={financeOptions}
                    classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-teal-500 focus:ring focus:ring-teal-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-teal-500"
          : "text-gray-500 hover:bg-teal-100 hover:text-teal-600 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isLoading}
                className="px-5 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
        </form>
      </div>
    </PageContent>
  );
};

export default MyInvestor;
